<template>
  <div
    ref="container"
    class="marquee"
    :style="{ '--marquee-width': marqueeWidth }"
  >
    <slot />
  </div>
</template>

<script>
  export default {
    name: 'MarqueeDisplay',
    props: {
      speed: { type: Number, default: 65 },
      wait: { type: Number, default: 1000 },
    },
    data() {
      return {
        marqueeWidth: '10px',
        marqueeAnimation: null,
        resizeObserver: null,
        mutationObserver: null,
        scheduled: false,
        lastCW: 0,
        lastTW: 0,
      };
    },
    methods: {
      scheduleUpdate() {
        if (this.scheduled) return;
        this.scheduled = true;
        requestAnimationFrame(() => {
          this.scheduled = false;
          this.updateMarquee();
        });
      },

      updateMarquee() {
        const { container } = this.$refs;
        if (!container) return;
        // find the <p> element inside the slot
        const txt = container.querySelector('p');
        if (!txt) return;

        const cw = Math.round(container.clientWidth || 0);
        const tw = Math.round(txt.scrollWidth || 0);

        if (cw === this.lastCW && tw === this.lastTW) return;
        this.lastCW = cw;
        this.lastTW = tw;

        const newVar = `${cw}px`;
        if (this.marqueeWidth !== newVar) this.marqueeWidth = newVar;

        if (this.marqueeAnimation) this.marqueeAnimation.cancel();

        if (tw <= cw) {
          txt.style.transform = '';
          txt.style.maskPosition = '';
          txt.style.maskImage = 'none';
          return;
        }

        txt.style.maskImage = '';
        const distance = tw - cw;
        const slideDuration = (distance / this.speed) * 1000;
        const totalDuration = this.wait * 2 + slideDuration;

        this.marqueeAnimation = txt.animate(
          [
            { transform: 'translateX(0)', maskPosition: '-5px 0', offset: 0 },
            {
              transform: 'translateX(0)',
              maskPosition: '-5px 0',
              offset: this.wait / totalDuration,
            },
            {
              transform: `translateX(-${distance}px)`,
              maskPosition: 'calc(100% + 5px) 0',
              offset: (this.wait + slideDuration) / totalDuration,
            },
            {
              transform: `translateX(-${distance}px)`,
              maskPosition: 'calc(100% + 5px) 0',
              offset: 1,
            },
          ],
          {
            duration: totalDuration,
            iterations: Infinity,
            easing: 'linear',
            direction: 'alternate',
          }
        );
      },

      initObservers() {
        this.resizeObserver = new ResizeObserver(() => {
          this.scheduleUpdate();
        });
        this.resizeObserver.observe(this.$refs.container, {
          box: 'border-box',
        });

        this.mutationObserver = new MutationObserver(() => {
          this.scheduleUpdate();
        });
        this.mutationObserver.observe(this.$refs.container, {
          childList: true,
          subtree: true,
          characterData: true,
        });

        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this.scheduleUpdate());
        }
        window.addEventListener('orientationchange', this.scheduleUpdate);
      },
    },
    mounted() {
      this.$nextTick(() => {
        this.updateMarquee();
        this.initObservers();
      });
    },
    beforeUnmount() {
      if (this.marqueeAnimation) this.marqueeAnimation.cancel();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.mutationObserver) this.mutationObserver.disconnect();
      window.removeEventListener('orientationchange', this.scheduleUpdate);
    },
  };
</script>

<style scoped>
  .marquee {
    position: relative;
    contain: layout paint;
    margin-bottom: 0;
    overflow: hidden;
    font-size: 0;
  }

  /* apply marquee-text styles to any <p> slotted inside */
  .marquee :deep(p) {
    display: inline-block;
    mask-image: linear-gradient(
      90deg,
      transparent 0,
      black 4%,
      black 96%,
      transparent 100%
    );
    mask-position: -5px 0;
    mask-size: calc(var(--marquee-width) + 5px) 100%;
    mask-repeat: no-repeat;
    will-change: transform;
    margin: 0;
    min-width: var(--marquee-width) !important;
    font-size: inherit;
    line-height: 1.2;
    user-select: none;
    white-space: nowrap;
  }
</style>
